import type {
  ColumnDataType,
  DatabaseBlockModel,
  DatabaseBlockProps,
  ImageBlockModel,
  ImageBlockProps,
} from '@blocksuite/affine-model';
import {
  generateFractionalIndexingKeyBetween,
  inferMimeTypeFromName,
  resolveBlobDisplayName,
} from '@blocksuite/affine-shared/utils';
import {
  Bound,
  isSerializedXYWH,
  type SerializedXYWH,
} from '@blocksuite/global/gfx';
import type { Store } from '@blocksuite/store';
import {
  createServerDatabaseRootDocId,
  FILE_INDEX_COLUMNS,
  FILE_INDEX_DATABASE_ID_PREFIX,
  FILE_INDEX_DATABASE_PROTECTION_PROPS,
  FILE_INDEX_MANAGED_BY,
  isFileIndexDatabaseId,
  isHiddenSystemManagedDatabase,
  isLocalDatabaseRootDocId,
  isServerDatabaseRootDocId,
  isServerDatabaseRowShardDocId,
  normalizeDatabaseShardManifest,
} from '@streamlain/database-identity';

import { createCanvasSummaryPresentationState } from './canvas-presentation-state.js';
import {
  createDatabaseBlockDataSource,
  type DatabaseBlockDataSource,
  provisionShardedDatabaseBlockDataSource,
} from './data-source.js';
import { textFromString as richTextFromString } from './properties/rich-text/define.js';
import { textFromString as titleTextFromString } from './properties/title/define.js';

export type FileIndexEnsureCaller = 'system' | 'user-action';

export type EnsureFileIndexDbInput = {
  workspaceId: string;
  docId: string;
  store: Store;
  visible?: boolean;
  caller: FileIndexEnsureCaller;
};

export type EnsureFileIndexDbResult = {
  databaseBlockId: string;
  storageBlockId: string;
  created: boolean;
  visible: boolean;
};

const FILE_INDEX_TITLE = 'Files';
const FILE_INDEX_VIEW_ID = 'file-index-table';
const FILE_INDEX_VIEW_SCHEMA_VERSION = 2;
const FILE_INDEX_DEFAULT_WIDTH = 960;
const FILE_INDEX_DEFAULT_HEIGHT = 420;
const FILE_INDEX_PLACEMENT_GAP = 80;
const DATABASE_SCHEMA_DEFAULT_XYWH: SerializedXYWH = '[0,0,420,300]';
const FILE_INDEX_SYSTEM_COLUMN_DATA = {
  systemManaged: true,
  managedBy: FILE_INDEX_MANAGED_BY,
} as const;
const FILE_INDEX_DATABASE_ROOT_PROTECTION_PROPS = {
  managedBy: FILE_INDEX_DATABASE_PROTECTION_PROPS.managedBy,
  systemManaged: FILE_INDEX_DATABASE_PROTECTION_PROPS.systemManaged,
} as const;

export type FileIndexDbVisibilityResult = EnsureFileIndexDbResult & {
  xywh: SerializedXYWH;
};

export type FileIndexDbTeardownResult = {
  databaseBlockId: string;
  storageBlockId: string | null;
  removed: boolean;
};

export type FileIndexSourceKind =
  | 'attachment'
  | 'audio'
  | 'image'
  | 'pdf'
  | 'video';

export type FileIndexSourceEntryStatus = 'ready' | 'needs-attention';

export type FileIndexSourceEntry = {
  sourceBlockId: string;
  blobId: string | null;
  name: string;
  mime: string;
  sizeBytes: number;
  sizeKb: number;
  kind: FileIndexSourceKind;
  extension: string;
  status: FileIndexSourceEntryStatus;
};

export type FileIndexRowUpsertResult = {
  rowId: string;
  created: boolean;
};

export type FileIndexReconcileResult = {
  databaseBlockId: string;
  storageBlockId: string;
  entries: FileIndexSourceEntry[];
  upserts: FileIndexRowUpsertResult[];
  deletedRowIds: string[];
  deferred: boolean;
};

export type FileIndexMirrorSubscription = {
  dispose: () => void;
  flush: () => FileIndexReconcileResult;
};

export const FILE_INDEX_FILES_ADDED_EVENT = 'streamlain:file-index-files-added';

export type FileIndexFilesAddedEventDetail = {
  workspaceId: string;
  docId: string;
  count: number;
};

export type FileIndexDbState = {
  databaseBlockId: string | null;
  exists: boolean;
  fileCount: number;
  readyCount: number;
  needsAttentionCount: number;
  syncing: boolean;
  visible: boolean;
};

const FILE_INDEX_KIND_OPTIONS: Array<{
  id: FileIndexSourceKind;
  value: string;
  color: string;
}> = [
  { id: 'attachment', value: 'Attachment', color: '#64748b' },
  { id: 'audio', value: 'Audio', color: '#8b5cf6' },
  { id: 'image', value: 'Image', color: '#22c55e' },
  { id: 'pdf', value: 'PDF', color: '#ef4444' },
  { id: 'video', value: 'Video', color: '#0ea5e9' },
];

const LEGACY_FILE_INDEX_SYSTEM_COLUMN_IDS = new Set([
  'file-name',
  'file-created-at',
  'file-updated-at',
  'file-visible',
]);

const FILE_INDEX_COLUMN_DEFS: Array<{
  key: keyof typeof FILE_INDEX_COLUMNS;
  name: string;
  type: ColumnDataType['type'];
  width: number;
  defaultVisible: boolean;
}> = [
  {
    key: 'sourceBlockId',
    name: 'Source Block',
    type: 'rich-text',
    width: 180,
    defaultVisible: false,
  },
  {
    key: 'blobId',
    name: 'Attachment',
    type: 'attachment',
    width: 220,
    defaultVisible: true,
  },
  {
    key: 'mime',
    name: 'MIME Type',
    type: 'rich-text',
    width: 180,
    defaultVisible: true,
  },
  {
    key: 'size',
    name: 'Size KB',
    type: 'number',
    width: 120,
    defaultVisible: true,
  },
  {
    key: 'kind',
    name: 'Kind',
    type: 'select',
    width: 120,
    defaultVisible: true,
  },
  {
    key: 'extension',
    name: 'Extension',
    type: 'rich-text',
    width: 120,
    defaultVisible: true,
  },
];

function encodeIdentifier(value: string): string {
  return Array.from(value)
    .map(char => char.codePointAt(0)?.toString(16).padStart(2, '0') ?? '')
    .join('');
}

export function createFileIndexDatabaseId(docId: string): string {
  if (!docId) {
    throw new Error('createFileIndexDatabaseId: docId is required');
  }
  return `${FILE_INDEX_DATABASE_ID_PREFIX}-${encodeIdentifier(docId)}`;
}

function isDatabaseSystemDocId(docId: string): boolean {
  if (!docId.startsWith('db$')) return false;
  const parts = docId.split('$');
  const databaseIndex = parts.indexOf('database');
  if (databaseIndex < 0) return false;
  const tail = parts.slice(databaseIndex + 1);
  return (
    (tail.length === 2 && tail[1] === 'root') ||
    (tail.length === 3 && tail[1] === 'rows' && tail[2] !== '')
  );
}

export function isFileIndexEligibleDocId(
  workspaceId: string,
  docId: string
): boolean {
  if (!workspaceId || !docId) return false;
  return (
    !isDatabaseSystemDocId(docId) &&
    !isServerDatabaseRootDocId(docId, workspaceId) &&
    !isServerDatabaseRowShardDocId(docId, workspaceId) &&
    !isLocalDatabaseRootDocId(docId)
  );
}

function createFileIndexRowId(sourceBlockId: string): string {
  if (!sourceBlockId) {
    throw new Error('createFileIndexRowId: sourceBlockId is required');
  }
  return `file-index-row-${encodeIdentifier(sourceBlockId)}`;
}

function extractFileExtension(name: string, mime: string): string {
  const lastSegment = name.split(/[\\/]/).at(-1) ?? name;
  const dotIndex = lastSegment.lastIndexOf('.');
  if (dotIndex >= 0 && dotIndex < lastSegment.length - 1) {
    return lastSegment.slice(dotIndex + 1).toLowerCase();
  }
  if (mime === 'application/pdf') return 'pdf';
  const slashIndex = mime.indexOf('/');
  return slashIndex >= 0 ? mime.slice(slashIndex + 1).toLowerCase() : '';
}

function sizeBytesToKb(value: unknown): number {
  const bytes = normalizeSize(value);
  if (bytes === 0) return 0;
  return Math.round((bytes / 1024) * 10) / 10;
}

function buildFileIndexColumns(): ColumnDataType[] {
  return FILE_INDEX_COLUMN_DEFS.map(def => ({
    id: FILE_INDEX_COLUMNS[def.key],
    name: def.name,
    type: def.type,
    data:
      def.key === 'kind'
        ? {
            ...FILE_INDEX_SYSTEM_COLUMN_DATA,
            options: FILE_INDEX_KIND_OPTIONS,
          }
        : { ...FILE_INDEX_SYSTEM_COLUMN_DATA },
  }));
}

function buildFileIndexView() {
  return {
    id: FILE_INDEX_VIEW_ID,
    name: 'Table View',
    mode: 'table',
    columns: [
      { id: 'title', hide: false, width: 260 },
      ...FILE_INDEX_COLUMN_DEFS.map(def => ({
        id: FILE_INDEX_COLUMNS[def.key],
        hide: !def.defaultVisible,
        width: def.width,
      })),
    ],
    filter: { type: 'group', op: 'and', conditions: [] },
    header: { titleColumn: 'title' },
  };
}

type FileIndexView = ReturnType<typeof buildFileIndexView>;

function mergeFileIndexView(
  existingView: unknown,
  migrateDefaultVisibility: boolean
): FileIndexView {
  const defaultView = buildFileIndexView();
  const defaultColumnsById = new Map(
    defaultView.columns.map(column => [column.id, column])
  );
  if (!existingView || typeof existingView !== 'object') {
    return defaultView;
  }

  const existing = existingView as Partial<FileIndexView>;
  const existingColumns = Array.isArray(existing.columns)
    ? existing.columns
    : [];
  const mergedColumns = new Map<string, FileIndexView['columns'][number]>();

  for (const column of existingColumns) {
    if (
      column &&
      typeof column === 'object' &&
      'id' in column &&
      typeof column.id === 'string'
    ) {
      if (LEGACY_FILE_INDEX_SYSTEM_COLUMN_IDS.has(column.id)) continue;
      const defaultColumn = defaultColumnsById.get(column.id);
      mergedColumns.set(
        column.id,
        migrateDefaultVisibility && defaultColumn
          ? { ...column, hide: defaultColumn.hide }
          : column
      );
    }
  }
  for (const column of defaultView.columns) {
    if (!mergedColumns.has(column.id)) {
      mergedColumns.set(column.id, column);
    }
  }

  const { iconColumn: _ignoredIconColumn, ...existingHeader } =
    (existing.header ?? {}) as Record<string, unknown>;

  return {
    ...defaultView,
    ...existing,
    id: FILE_INDEX_VIEW_ID,
    mode: 'table',
    columns: [...mergedColumns.values()],
    filter: existing.filter ?? defaultView.filter,
    header: {
      ...defaultView.header,
      ...existingHeader,
    },
  };
}

function areJsonEqual(left: unknown, right: unknown): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

function normalizeAttachmentCell(value: unknown): unknown {
  if (!value || typeof value !== 'object') {
    return null;
  }

  return Object.entries(value as Record<string, unknown>)
    .map(([blobId, file]) => {
      const entry =
        file && typeof file === 'object'
          ? (file as Record<string, unknown>)
          : {};
      return {
        blobId,
        id: entry.id,
        name: entry.name,
        mime: entry.mime,
        size: entry.size,
      };
    })
    .sort((left, right) => left.blobId.localeCompare(right.blobId));
}

function writeCellIfChanged(
  dataSource: DatabaseBlockDataSource,
  rowId: string,
  columnId: string,
  nextValue: unknown,
  equal: (currentValue: unknown, nextValue: unknown) => boolean = Object.is
): void {
  const currentValue = dataSource.cellValueGet(rowId, columnId);
  if (equal(currentValue, nextValue)) {
    return;
  }
  dataSource.cellValueChange(rowId, columnId, nextValue, 'system');
}

function ensureFileIndexColumns(model: DatabaseBlockModel): void {
  const requiredColumns = buildFileIndexColumns();
  const requiredById = new Map(
    requiredColumns.map(column => [column.id, column])
  );
  const existingColumns = Array.isArray(model.props.columns)
    ? model.props.columns
    : [];
  const mergedColumns = existingColumns
    .filter(column => !LEGACY_FILE_INDEX_SYSTEM_COLUMN_IDS.has(column.id))
    .map(column => {
      const requiredColumn = requiredById.get(column.id);
      if (!requiredColumn) return column;
      return {
        ...column,
        name: requiredColumn.name,
        type: requiredColumn.type,
        data: requiredColumn.data,
      };
    });
  const existingIds = new Set(mergedColumns.map(column => column.id));
  for (const requiredColumn of requiredColumns) {
    if (!existingIds.has(requiredColumn.id)) {
      mergedColumns.push(requiredColumn);
    }
  }
  if (!areJsonEqual(model.props.columns, mergedColumns)) {
    model.props.columns = mergedColumns;
  }
}

function ensureFileIndexView(model: DatabaseBlockModel): void {
  const views = model.props.views as FileIndexView[];
  const fileIndexViewIndexes: number[] = [];
  const migrateDefaultVisibility =
    model.props.fileIndexViewVersion !== FILE_INDEX_VIEW_SCHEMA_VERSION;

  views.forEach((view, index) => {
    if (view?.id === FILE_INDEX_VIEW_ID) {
      fileIndexViewIndexes.push(index);
    }
  });

  if (fileIndexViewIndexes.length === 0) {
    views.splice(0, 0, buildFileIndexView());
    model.props.fileIndexViewVersion = FILE_INDEX_VIEW_SCHEMA_VERSION;
    return;
  }

  const primaryIndex = fileIndexViewIndexes[0]!;
  const currentView = views[primaryIndex];
  const nextView = mergeFileIndexView(currentView, migrateDefaultVisibility);
  if (!areJsonEqual(currentView, nextView)) {
    views.splice(primaryIndex, 1, nextView);
  }

  for (let index = fileIndexViewIndexes.length - 1; index >= 1; index--) {
    const duplicateIndex = fileIndexViewIndexes[index]!;
    if (duplicateIndex < views.length) {
      views.splice(duplicateIndex, 1);
    }
  }
  if (model.props.fileIndexViewVersion !== FILE_INDEX_VIEW_SCHEMA_VERSION) {
    model.props.fileIndexViewVersion = FILE_INDEX_VIEW_SCHEMA_VERSION;
  }
}

function isUsableFileIndexXYWH(value: unknown): value is SerializedXYWH {
  if (typeof value !== 'string' || value === DATABASE_SCHEMA_DEFAULT_XYWH) {
    return false;
  }

  return isSerializedXYWH(value);
}

function readCurrentFileIndexXYWH(
  model: DatabaseBlockModel
): SerializedXYWH | null {
  const xywh =
    typeof model.xywh$?.value === 'string'
      ? model.xywh$.value
      : model.props.xywh;
  return isUsableFileIndexXYWH(xywh) ? xywh : null;
}

function readValidFileIndexXYWH(
  model: DatabaseBlockModel
): SerializedXYWH | null {
  const xywh =
    typeof model.xywh$?.value === 'string'
      ? model.xywh$.value
      : model.props.xywh;
  return isSerializedXYWH(xywh) ? xywh : null;
}

function readLastFileIndexXYWH(
  model: DatabaseBlockModel
): SerializedXYWH | null {
  const xywh = model.props.fileIndexLastXYWH;
  return isUsableFileIndexXYWH(xywh) ? xywh : null;
}

function findFileIndexDatabaseBlockId(
  store: Store,
  expectedDatabaseId: string
): string | null {
  const yDoc = (store as unknown as { spaceDoc?: unknown; yDoc?: unknown })
    .spaceDoc as
    | {
        getMap?: (name: string) => {
          forEach?: (cb: (value: unknown, key: string) => void) => void;
        };
      }
    | undefined;
  const fallbackYDoc = (store as unknown as { yDoc?: unknown }).yDoc as
    | {
        getMap?: (name: string) => {
          forEach?: (cb: (value: unknown, key: string) => void) => void;
        };
      }
    | undefined;
  const blocks = (yDoc ?? fallbackYDoc)?.getMap?.('blocks');
  if (!blocks?.forEach) return null;

  let found: string | null = null;
  blocks.forEach((blockMap: unknown, blockId: string) => {
    if (found) return;
    const map = blockMap as { get?: (key: string) => unknown };
    if (map.get?.('sys:flavour') !== 'affine:database') return;
    const modelProps = store.getBlock(blockId)?.model?.props as
      | Record<string, unknown>
      | undefined;
    const managedBy =
      map.get?.('prop:managedBy') ??
      map.get?.('managedBy') ??
      modelProps?.managedBy;
    if (managedBy === FILE_INDEX_MANAGED_BY && blockId === expectedDatabaseId) {
      found = blockId;
    }
  });
  return found;
}

function deleteNonCanonicalFileIndexHostBlocks(
  store: Store,
  expectedDatabaseId: string
): string[] {
  const blockIds: string[] = [];
  forEachStoreBlockModel(store, model => {
    if (
      model.flavour === 'affine:database' &&
      model.props.managedBy === FILE_INDEX_MANAGED_BY &&
      model.id !== expectedDatabaseId
    ) {
      blockIds.push(model.id);
    }
  });

  if (blockIds.length === 0) return [];
  store.withoutTransact(() => {
    for (const blockId of blockIds) {
      const block = store.getBlock(blockId);
      if (block?.model) {
        store.deleteBlock(block.model);
      }
    }
  });
  return blockIds;
}

function readExistingSystemWorkspaceStore(
  hostDoc: Store,
  docId: string
): Store | undefined {
  const docRecord = hostDoc.workspace.getDoc(docId);
  if (!docRecord) {
    return undefined;
  }

  const systemStore = docRecord.getStore({ id: docId });
  if (!hostDoc.schema) return systemStore;

  const missingSchemas = Array.from(
    hostDoc.schema.flavourSchemaMap.values()
  ).filter(
    schema => !systemStore.schema.flavourSchemaMap.has(schema.model.flavour)
  );
  if (missingSchemas.length > 0) {
    systemStore.schema.register(missingSchemas);
  }
  return systemStore;
}

function getCanonicalFileIndexRootDocId(
  workspaceId: string,
  databaseId: string
): string {
  return createServerDatabaseRootDocId({ workspaceId, databaseId });
}

function readCanonicalFileIndexStorageModel(
  store: Store,
  databaseId: string
): DatabaseBlockModel | undefined {
  const rootDocId = getCanonicalFileIndexRootDocId(
    store.workspace.id,
    databaseId
  );
  return readExistingSystemWorkspaceStore(store, rootDocId)?.getBlock(
    databaseId
  )?.model as DatabaseBlockModel | undefined;
}

type FileIndexCanonicalIdentityStatus =
  | 'invalid'
  | 'pending'
  | 'repairable'
  | 'valid';

function resolveCanonicalFileIndexIdentity(
  store: Store,
  hostModel: DatabaseBlockModel,
  databaseId: string
): FileIndexCanonicalIdentityStatus {
  if (hostModel.id !== databaseId) return 'invalid';
  const rootDocId = getCanonicalFileIndexRootDocId(
    store.workspace.id,
    databaseId
  );
  if (hostModel.props.databaseId !== databaseId) return 'invalid';
  if (hostModel.props.databaseRootDocId !== rootDocId) return 'invalid';

  const storageModel = readCanonicalFileIndexStorageModel(store, databaseId);
  if (!storageModel) return 'pending';
  if (storageModel.flavour !== 'affine:database') return 'invalid';
  if (storageModel.id !== databaseId) return 'invalid';
  if (storageModel.props.databaseId !== databaseId) return 'invalid';
  if (storageModel.props.databaseRootDocId !== rootDocId) return 'invalid';

  try {
    const manifest = normalizeDatabaseShardManifest(
      storageModel.props.databaseShardManifest
    );
    return manifest?.databaseId === databaseId &&
      manifest.databaseRootDocId === rootDocId &&
      manifest.workspaceId === store.workspace.id
      ? 'valid'
      : 'repairable';
  } catch {
    return 'repairable';
  }
}

function deleteCanonicalFileIndexDatabase(
  store: Store,
  databaseId: string
): void {
  const hostBlock = store.getBlock(databaseId);
  if (hostBlock?.model?.flavour === 'affine:database') {
    store.withoutTransact(() => {
      store.deleteBlock(hostBlock.model);
    });
  }

  const storageModel = readCanonicalFileIndexStorageModel(store, databaseId);
  if (storageModel?.flavour === 'affine:database') {
    storageModel.store.withoutTransact(() => {
      storageModel.store.deleteBlock(storageModel);
    });
  }
}

function forEachStoreBlock(
  store: Store,
  callback: (block: {
    id: string;
    flavour: string;
    model: { props: Record<string, unknown> };
  }) => void
) {
  const yDoc = (store as unknown as { spaceDoc?: unknown; yDoc?: unknown })
    .spaceDoc as
    | {
        getMap?: (name: string) => {
          forEach?: (cb: (value: unknown, key: string) => void) => void;
        };
      }
    | undefined;
  const fallbackYDoc = (store as unknown as { yDoc?: unknown }).yDoc as
    | {
        getMap?: (name: string) => {
          forEach?: (cb: (value: unknown, key: string) => void) => void;
        };
      }
    | undefined;
  const blocks = (yDoc ?? fallbackYDoc)?.getMap?.('blocks');
  blocks?.forEach?.((_blockMap: unknown, blockId: string) => {
    const model = store.getBlock(blockId)?.model as
      | { flavour: string; props: Record<string, unknown> }
      | undefined;
    if (model) callback({ id: blockId, flavour: model.flavour, model });
  });
}

function forEachStoreBlockModel(
  store: Store,
  callback: (model: {
    id: string;
    flavour: string;
    props: Record<string, unknown>;
  }) => void
) {
  forEachStoreBlock(store, block =>
    callback({
      id: block.id,
      flavour: block.flavour,
      props: block.model.props,
    })
  );
}

function readVisible(model: DatabaseBlockModel): boolean {
  return model.props.visible === true;
}

export function isFileIndexDatabase(model: DatabaseBlockModel): boolean {
  return (
    model.props.managedBy === FILE_INDEX_MANAGED_BY ||
    isFileIndexDatabaseId(model.id)
  );
}

export function isHiddenSystemManagedDatabaseBlock(
  model: DatabaseBlockModel
): boolean {
  return isHiddenSystemManagedDatabase({
    id: model.id,
    managedBy: model.props.managedBy,
    visible: model.props.visible,
  });
}

export function isHiddenFileIndexDatabase(model: DatabaseBlockModel): boolean {
  return isHiddenSystemManagedDatabaseBlock(model);
}

export function getFileIndexDbState(store: Store): FileIndexDbState {
  const databaseBlockId = findFileIndexDatabaseBlockId(
    store,
    createFileIndexDatabaseId(store.id)
  );
  const model = databaseBlockId
    ? (store.getBlock(databaseBlockId)?.model as DatabaseBlockModel | undefined)
    : undefined;
  const entries = scanFileIndexSourceBlocks(store);
  const readyCount = entries.filter(entry => entry.status === 'ready').length;
  const needsAttentionCount = entries.length - readyCount;

  return {
    databaseBlockId,
    exists: Boolean(databaseBlockId),
    fileCount: entries.length,
    readyCount,
    needsAttentionCount,
    syncing: false,
    visible: model ? readVisible(model) : false,
  };
}

function resolveVisible(
  input: EnsureFileIndexDbInput,
  model?: DatabaseBlockModel
) {
  if (input.caller === 'system') {
    if (input.visible === true) {
      throw new Error(
        'ensureFileIndexDb: system caller cannot show File Index'
      );
    }
    return model ? readVisible(model) : false;
  }
  return input.visible ?? (model ? readVisible(model) : false);
}

function writeProtectionProps(
  store: Store,
  model: DatabaseBlockModel,
  visible: boolean,
  xywh?: SerializedXYWH
) {
  const stableXYWH =
    xywh ?? readValidFileIndexXYWH(model) ?? DATABASE_SCHEMA_DEFAULT_XYWH;
  const props = {
    ...FILE_INDEX_DATABASE_PROTECTION_PROPS,
    visible,
    ...(model.props.title?.toString() === FILE_INDEX_TITLE
      ? {}
      : { title: titleTextFromString(FILE_INDEX_TITLE) }),
    xywh: stableXYWH,
    ...(xywh ? { fileIndexLastXYWH: xywh } : {}),
  };
  const changedProps = Object.fromEntries(
    Object.entries(props).filter(([key, value]) => {
      const current = model.props[key as keyof DatabaseBlockProps];
      if (current && value && key === 'title') {
        return current.toString() !== value.toString();
      }
      return !areJsonEqual(current, value);
    })
  );
  if (Object.keys(changedProps).length === 0) return;
  store.withoutTransact(() => {
    store.updateBlock(model, changedProps);
  });
}

function repairMissingFileIndexCanvasGeometry(
  store: Store,
  model: DatabaseBlockModel,
  visible: boolean
): SerializedXYWH | null {
  if (readValidFileIndexXYWH(model)) return null;

  const lastXYWH = readLastFileIndexXYWH(model);
  const xywh =
    lastXYWH ??
    (visible
      ? computeDefaultFileIndexXYWH(store)
      : DATABASE_SCHEMA_DEFAULT_XYWH);
  const props = {
    xywh,
    ...(isUsableFileIndexXYWH(xywh) ? { fileIndexLastXYWH: xywh } : {}),
  };
  store.withoutTransact(() => {
    store.updateBlock(model, props);
  });
  return xywh;
}

type EnsuredFileIndexDbDataSourceResult = EnsureFileIndexDbResult & {
  dataSource: DatabaseBlockDataSource;
};

function writeStorageSchema(dataSource: DatabaseBlockDataSource) {
  const storageModel = dataSource.storageModel as DatabaseBlockModel;
  storageModel.store.withoutTransact(() => {
    if (
      !areJsonEqual(
        storageModel.props.managedBy,
        FILE_INDEX_DATABASE_ROOT_PROTECTION_PROPS.managedBy
      )
    ) {
      storageModel.props.managedBy =
        FILE_INDEX_DATABASE_ROOT_PROTECTION_PROPS.managedBy;
    }
    if (
      !areJsonEqual(
        storageModel.props.systemManaged,
        FILE_INDEX_DATABASE_ROOT_PROTECTION_PROPS.systemManaged
      )
    ) {
      storageModel.props.systemManaged =
        FILE_INDEX_DATABASE_ROOT_PROTECTION_PROPS.systemManaged;
    }
    ensureFileIndexColumns(storageModel);
    ensureFileIndexView(storageModel);
    if (storageModel.props.canvasPresentation == null) {
      storageModel.props.canvasPresentation =
        createCanvasSummaryPresentationState();
    }

    if (storageModel.props.title?.toString() !== FILE_INDEX_TITLE) {
      storageModel.props.title = titleTextFromString(FILE_INDEX_TITLE);
    }
  });
  return storageModel.id;
}

function assertInput(input: EnsureFileIndexDbInput) {
  if (!input.workspaceId || !input.docId) {
    throw new Error('ensureFileIndexDb: workspaceId and docId are required');
  }
  if (!input.store?.root) {
    throw new Error('ensureFileIndexDb: store.root is undefined');
  }
  if (input.store.workspace.id !== input.workspaceId) {
    throw new Error('ensureFileIndexDb: workspaceId does not match store');
  }
  if (input.store.id !== input.docId) {
    throw new Error('ensureFileIndexDb: docId does not match store');
  }
  if (!isFileIndexEligibleDocId(input.workspaceId, input.docId)) {
    throw new Error(
      `ensureFileIndexDb: File Index is only valid for page docs (${input.docId})`
    );
  }
}

function readBoundFromModel(model: { props: Record<string, unknown> }) {
  if (model.props.managedBy === FILE_INDEX_MANAGED_BY) return null;
  if (typeof model.props.xywh !== 'string') return null;
  return Bound.deserialize(model.props.xywh);
}

function computeDefaultFileIndexXYWH(store: Store): SerializedXYWH {
  let minY = 0;
  let maxRight = 0;
  let found = false;

  forEachStoreBlockModel(store, model => {
    const bound = readBoundFromModel(model);
    if (!bound) return;
    if (!found) {
      minY = bound.y;
      maxRight = bound.x + bound.w;
      found = true;
      return;
    }
    minY = Math.min(minY, bound.y);
    maxRight = Math.max(maxRight, bound.x + bound.w);
  });

  return new Bound(
    maxRight + FILE_INDEX_PLACEMENT_GAP,
    minY,
    FILE_INDEX_DEFAULT_WIDTH,
    FILE_INDEX_DEFAULT_HEIGHT
  ).serialize();
}

function resolveShowXYWH(
  store: Store,
  model: DatabaseBlockModel,
  xywh?: SerializedXYWH
): SerializedXYWH {
  if (xywh) return xywh;
  const currentXYWH = readCurrentFileIndexXYWH(model);
  if (currentXYWH) return currentXYWH;
  const lastXYWH = readLastFileIndexXYWH(model);
  if (lastXYWH) return lastXYWH;
  return computeDefaultFileIndexXYWH(store);
}

function normalizeSize(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0
    ? value
    : 0;
}

function resolveAttachmentKind(mime: string): FileIndexSourceKind {
  if (mime === 'application/pdf') return 'pdf';
  if (mime.startsWith('audio/')) return 'audio';
  if (mime.startsWith('video/')) return 'video';
  if (mime.startsWith('image/')) return 'image';
  return 'attachment';
}

function isFileIndexSourceFlavour(flavour: string): boolean {
  return flavour === 'affine:attachment' || flavour === 'affine:image';
}

function isFileIndexSourceUpdateKey(key: string): boolean {
  return (
    key === 'name' || key === 'size' || key === 'sourceId' || key === 'type'
  );
}

function attachmentCell(entry: FileIndexSourceEntry) {
  if (!entry.blobId) return undefined;
  return {
    [entry.blobId]: {
      id: entry.blobId,
      name: entry.name,
      mime: entry.mime,
      size: entry.sizeBytes,
      order: generateFractionalIndexingKeyBetween(null, null),
    },
  };
}

function readCellText(
  dataSource: DatabaseBlockDataSource,
  rowId: string,
  columnId: string
): string {
  const value = dataSource.cellValueGet(rowId, columnId);
  if (typeof value === 'string') return value;
  if (!value || typeof value !== 'object') return '';

  const richText = value as { yText?: unknown; toString?: () => string };
  const yText = richText.yText;
  if (
    yText &&
    typeof yText === 'object' &&
    typeof (yText as { toString?: unknown }).toString === 'function'
  ) {
    return (yText as { toString: () => string }).toString();
  }

  return typeof richText.toString === 'function' ? richText.toString() : '';
}

function writeRichTextCellIfChanged(
  dataSource: DatabaseBlockDataSource,
  rowId: string,
  columnId: string,
  nextText: string
): void {
  if (readCellText(dataSource, rowId, columnId) === nextText) {
    return;
  }
  dataSource.cellValueChange(
    rowId,
    columnId,
    richTextFromString(nextText),
    'system'
  );
}

function canReadLogicalRows(dataSource: DatabaseBlockDataSource): boolean {
  return (
    dataSource.getLogicalRowIdsSnapshot().length > 0 ||
    dataSource.rowCount$.value === 0
  );
}

function findFileIndexRowBySourceBlockId(
  dataSource: DatabaseBlockDataSource,
  sourceBlockId: string
): string | null {
  return (
    dataSource
      .getLogicalRowIdsSnapshot()
      .find(
        rowId =>
          readCellText(dataSource, rowId, FILE_INDEX_COLUMNS.sourceBlockId) ===
          sourceBlockId
      ) ?? null
  );
}

function uniqueNonEmpty(values: readonly string[]): string[] {
  const seen = new Set<string>();
  const unique: string[] = [];
  for (const value of values) {
    const trimmed = value.trim();
    if (!trimmed || seen.has(trimmed)) continue;
    seen.add(trimmed);
    unique.push(trimmed);
  }
  return unique;
}

export function getFileIndexSourceBlockIdsForRows(
  fileIndexModel: DatabaseBlockModel,
  rowIds: readonly string[]
): string[] {
  if (!isFileIndexDatabase(fileIndexModel) || rowIds.length === 0) {
    return [];
  }

  const dataSource = createDatabaseBlockDataSource(fileIndexModel);
  return uniqueNonEmpty(
    rowIds.map(rowId =>
      readCellText(dataSource, rowId, FILE_INDEX_COLUMNS.sourceBlockId)
    )
  );
}

export function getFileIndexRowIdsForSourceBlocks(
  fileIndexModel: DatabaseBlockModel,
  sourceBlockIds: readonly string[]
): string[] {
  if (!isFileIndexDatabase(fileIndexModel) || sourceBlockIds.length === 0) {
    return [];
  }

  const sourceBlockIdSet = new Set(uniqueNonEmpty(sourceBlockIds));
  if (sourceBlockIdSet.size === 0) {
    return [];
  }

  const dataSource = createDatabaseBlockDataSource(fileIndexModel);
  return dataSource.getLogicalRowIdsSnapshot().filter(rowId => {
    const sourceBlockId = readCellText(
      dataSource,
      rowId,
      FILE_INDEX_COLUMNS.sourceBlockId
    );
    return sourceBlockIdSet.has(sourceBlockId);
  });
}

export function scanFileIndexSourceBlocks(
  store: Store
): FileIndexSourceEntry[] {
  const entries: FileIndexSourceEntry[] = [];

  forEachStoreBlock(store, ({ id, flavour, model }) => {
    if (flavour !== 'affine:attachment' && flavour !== 'affine:image') return;
    if (model.props.managedBy === FILE_INDEX_MANAGED_BY) return;

    const rawSourceId = model.props.sourceId;
    const sourceId = typeof rawSourceId === 'string' ? rawSourceId : '';

    if (flavour === 'affine:image') {
      const occurrenceName =
        typeof model.props.name === 'string' && model.props.name.trim()
          ? model.props.name.trim()
          : '';
      const rawMime =
        typeof model.props.type === 'string' && model.props.type.trim()
          ? model.props.type.trim()
          : '';
      const mime =
        rawMime && rawMime !== 'application/octet-stream'
          ? rawMime
          : (inferMimeTypeFromName(occurrenceName) ??
            'application/octet-stream');
      const name = resolveBlobDisplayName({
        occurrenceName,
        fallbackName: 'image',
        mimeType: mime,
      });
      const sizeBytes = normalizeSize(model.props.size);
      entries.push({
        sourceBlockId: id,
        blobId: sourceId || null,
        name,
        mime,
        sizeBytes,
        sizeKb: sizeBytesToKb(sizeBytes),
        kind: 'image',
        extension: extractFileExtension(name, mime),
        status: sourceId ? 'ready' : 'needs-attention',
      });
      return;
    }

    const name =
      typeof model.props.name === 'string' && model.props.name.trim()
        ? model.props.name.trim()
        : 'Attachment';
    const rawMime =
      typeof model.props.type === 'string' && model.props.type.trim()
        ? model.props.type.trim()
        : '';
    const mime =
      rawMime && rawMime !== 'application/octet-stream'
        ? rawMime
        : (inferMimeTypeFromName(name) ?? 'application/octet-stream');
    const sizeBytes = normalizeSize(model.props.size);
    entries.push({
      sourceBlockId: id,
      blobId: sourceId || null,
      name,
      mime,
      sizeBytes,
      sizeKb: sizeBytesToKb(sizeBytes),
      kind: resolveAttachmentKind(mime),
      extension: extractFileExtension(name, mime),
      status: sourceId ? 'ready' : 'needs-attention',
    });
  });

  return entries;
}

export async function hydrateMissingImageFileMetadata(
  store: Store
): Promise<number> {
  if (store.readonly) return 0;

  const candidates: Array<{
    sourceBlockId: string;
    sourceId: string;
  }> = [];
  forEachStoreBlock(store, ({ id, flavour, model }) => {
    if (flavour !== 'affine:image') return;
    const sourceId =
      typeof model.props.sourceId === 'string' ? model.props.sourceId : '';
    const hasMime =
      typeof model.props.type === 'string' &&
      model.props.type.startsWith('image/');
    const hasSize =
      typeof model.props.size === 'number' && model.props.size >= 0;
    if (sourceId && (!hasMime || !hasSize)) {
      candidates.push({ sourceBlockId: id, sourceId });
    }
  });
  if (candidates.length === 0) return 0;

  const sourceIds = [
    ...new Set(candidates.map(candidate => candidate.sourceId)),
  ];
  const metadataBySourceId = new Map<
    string,
    Awaited<ReturnType<typeof store.workspace.blobSync.getMetadata>>
  >();
  let nextIndex = 0;
  const worker = async () => {
    while (nextIndex < sourceIds.length) {
      const sourceId = sourceIds[nextIndex++];
      if (!sourceId) continue;
      try {
        metadataBySourceId.set(
          sourceId,
          await store.workspace.blobSync.getMetadata(sourceId)
        );
      } catch {
        metadataBySourceId.set(sourceId, null);
      }
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(4, sourceIds.length) }, () => worker())
  );

  let updated = 0;
  store.withoutTransact(() => {
    for (const candidate of candidates) {
      const block = store.getBlock(candidate.sourceBlockId);
      if (!block || block.model.flavour !== 'affine:image') continue;
      const model = block.model as ImageBlockModel;
      if (model.props.sourceId !== candidate.sourceId) continue;
      const metadata = metadataBySourceId.get(candidate.sourceId);
      if (!metadata) continue;

      const props: Partial<ImageBlockProps> = {};
      if (
        (!(typeof model.props.type === 'string') ||
          !model.props.type.startsWith('image/')) &&
        metadata.mime.startsWith('image/')
      ) {
        props.type = metadata.mime;
      }
      if (
        (!(typeof model.props.size === 'number') || model.props.size < 0) &&
        Number.isSafeInteger(metadata.size) &&
        metadata.size >= 0
      ) {
        props.size = metadata.size;
      }
      if (Object.keys(props).length > 0) {
        store.updateBlock(model, props);
        updated++;
      }
    }
  });
  return updated;
}

export function upsertFileIndexRow(
  fileIndexModel: DatabaseBlockModel,
  entry: FileIndexSourceEntry
): FileIndexRowUpsertResult {
  return upsertFileIndexRowWithDataSource(
    createDatabaseBlockDataSource(fileIndexModel),
    entry
  );
}

function upsertFileIndexRowWithDataSource(
  dataSource: DatabaseBlockDataSource,
  entry: FileIndexSourceEntry
): FileIndexRowUpsertResult {
  if (entry.status !== 'ready' || !entry.blobId) {
    throw new Error(
      `upsertFileIndexRow: source block "${entry.sourceBlockId}" has no blob id`
    );
  }

  const storageModel = dataSource.storageModel as DatabaseBlockModel;
  let result: FileIndexRowUpsertResult | null = null;

  storageModel.store.withoutTransact(() => {
    let rowId = findFileIndexRowBySourceBlockId(
      dataSource,
      entry.sourceBlockId
    );
    let created = false;
    if (!rowId) {
      rowId = createFileIndexRowId(entry.sourceBlockId);
      created =
        dataSource.rowAddManyWithCellValues(
          [
            {
              rowId,
              values: {
                title: entry.name,
                [FILE_INDEX_COLUMNS.sourceBlockId]: richTextFromString(
                  entry.sourceBlockId
                ),
                [FILE_INDEX_COLUMNS.blobId]: { ...attachmentCell(entry) },
                [FILE_INDEX_COLUMNS.mime]: richTextFromString(entry.mime),
                [FILE_INDEX_COLUMNS.size]: entry.sizeKb,
                [FILE_INDEX_COLUMNS.kind]: entry.kind,
                [FILE_INDEX_COLUMNS.extension]: richTextFromString(
                  entry.extension
                ),
              },
            },
          ],
          'end',
          'system'
        ) === 1;
      result = { rowId, created };
      return;
    }

    if (readCellText(dataSource, rowId, 'title') !== entry.name) {
      dataSource.cellValueChange(rowId, 'title', entry.name, 'system');
    }
    writeRichTextCellIfChanged(
      dataSource,
      rowId,
      FILE_INDEX_COLUMNS.sourceBlockId,
      entry.sourceBlockId
    );
    const nextAttachmentCell = { ...attachmentCell(entry) };
    writeCellIfChanged(
      dataSource,
      rowId,
      FILE_INDEX_COLUMNS.blobId,
      nextAttachmentCell,
      (currentValue, nextValue) =>
        areJsonEqual(
          normalizeAttachmentCell(currentValue),
          normalizeAttachmentCell(nextValue)
        )
    );
    writeRichTextCellIfChanged(
      dataSource,
      rowId,
      FILE_INDEX_COLUMNS.mime,
      entry.mime
    );
    writeCellIfChanged(
      dataSource,
      rowId,
      FILE_INDEX_COLUMNS.size,
      entry.sizeKb
    );
    writeCellIfChanged(dataSource, rowId, FILE_INDEX_COLUMNS.kind, entry.kind);
    writeRichTextCellIfChanged(
      dataSource,
      rowId,
      FILE_INDEX_COLUMNS.extension,
      entry.extension
    );

    result = { rowId, created };
  });

  if (!result) {
    throw new Error('upsertFileIndexRow: row upsert failed');
  }
  return result;
}

function deleteFileIndexRowsNotInSourceSet(
  dataSource: DatabaseBlockDataSource,
  activeSourceBlockIds: Set<string>
): string[] {
  const storageModel = dataSource.storageModel as DatabaseBlockModel;
  const seenActiveSourceBlockIds = new Set<string>();
  const deletedRowIds = dataSource.getLogicalRowIdsSnapshot().filter(rowId => {
    const sourceBlockId = readCellText(
      dataSource,
      rowId,
      FILE_INDEX_COLUMNS.sourceBlockId
    );
    if (!sourceBlockId) return false;
    if (!activeSourceBlockIds.has(sourceBlockId)) return true;
    if (seenActiveSourceBlockIds.has(sourceBlockId)) return true;
    seenActiveSourceBlockIds.add(sourceBlockId);
    return false;
  });

  if (deletedRowIds.length) {
    storageModel.store.withoutTransact(() => {
      dataSource.rowDelete(deletedRowIds, 'system');
    });
  }

  return deletedRowIds;
}

export function reconcileFileIndexDb(
  input: Omit<EnsureFileIndexDbInput, 'caller' | 'visible'>
): FileIndexReconcileResult {
  assertInput({ ...input, caller: 'system' });
  const databaseId = createFileIndexDatabaseId(input.docId);
  deleteNonCanonicalFileIndexHostBlocks(input.store, databaseId);
  if (
    input.store.doc.yBlocks.has(databaseId) &&
    !input.store.hasBlock(databaseId)
  ) {
    return {
      databaseBlockId: databaseId,
      storageBlockId: databaseId,
      entries: scanFileIndexSourceBlocks(input.store),
      upserts: [],
      deletedRowIds: [],
      deferred: true,
    };
  }
  const existingBlockId =
    input.store.getBlock(databaseId)?.model?.flavour === 'affine:database'
      ? databaseId
      : findFileIndexDatabaseBlockId(input.store, databaseId);
  const existingModel = existingBlockId
    ? (input.store.getBlock(existingBlockId)?.model as
        | DatabaseBlockModel
        | undefined)
    : undefined;
  if (
    existingModel &&
    resolveCanonicalFileIndexIdentity(
      input.store,
      existingModel,
      databaseId
    ) === 'pending'
  ) {
    return {
      databaseBlockId: existingBlockId ?? databaseId,
      storageBlockId: databaseId,
      entries: scanFileIndexSourceBlocks(input.store),
      upserts: [],
      deletedRowIds: [],
      deferred: true,
    };
  }

  const ensured = ensureFileIndexDbDataSource({
    ...input,
    caller: 'system',
  });
  const entries = scanFileIndexSourceBlocks(input.store);
  const dataSource = ensured.dataSource;
  const deferred = !canReadLogicalRows(dataSource);
  const activeSourceBlockIds = new Set(
    entries
      .filter(entry => entry.status === 'ready')
      .map(entry => entry.sourceBlockId)
  );
  const upserts = deferred
    ? []
    : entries
        .filter(entry => entry.status === 'ready')
        .map(entry => upsertFileIndexRowWithDataSource(dataSource, entry));
  const deletedRowIds = deferred
    ? []
    : deleteFileIndexRowsNotInSourceSet(dataSource, activeSourceBlockIds);

  return {
    databaseBlockId: ensured.databaseBlockId,
    storageBlockId: ensured.storageBlockId,
    entries,
    upserts,
    deletedRowIds,
    deferred,
  };
}

export function createFileIndexMirrorSubscription(
  input: Omit<EnsureFileIndexDbInput, 'caller' | 'visible'>
): FileIndexMirrorSubscription {
  let disposed = false;
  let scheduled = false;
  let deferredRetry: ReturnType<typeof setTimeout> | undefined;
  let announceNextFlush = false;
  let canAnnounceUserChanges = false;
  let hydrationPromise: Promise<void> | undefined;
  let hydrationRequested = false;

  const announceTimer = setTimeout(() => {
    canAnnounceUserChanges = true;
  }, 1000);

  const announceFilesAdded = (result: FileIndexReconcileResult) => {
    if (!announceNextFlush || !canAnnounceUserChanges) return;
    const count = result.upserts.filter(upsert => upsert.created).length;
    if (count <= 0 || typeof window === 'undefined') return;
    window.dispatchEvent(
      new CustomEvent<FileIndexFilesAddedEventDetail>(
        FILE_INDEX_FILES_ADDED_EVENT,
        {
          detail: {
            workspaceId: input.workspaceId,
            docId: input.docId,
            count,
          },
        }
      )
    );
  };

  const flush = () => {
    if (disposed) {
      throw new Error('createFileIndexMirrorSubscription: disposed');
    }
    scheduled = false;
    if (deferredRetry) {
      clearTimeout(deferredRetry);
      deferredRetry = undefined;
    }
    const result = reconcileFileIndexDb(input);
    announceFilesAdded(result);
    announceNextFlush = false;
    if (result.deferred && !disposed) {
      deferredRetry = setTimeout(() => {
        if (!disposed) schedule();
      }, 250);
    }
    return result;
  };

  const schedule = () => {
    if (disposed || scheduled) return;
    scheduled = true;
    queueMicrotask(() => {
      if (!disposed) flush();
    });
  };

  const hydrateMetadata = () => {
    if (disposed) return;
    if (hydrationPromise) {
      hydrationRequested = true;
      return;
    }
    hydrationRequested = false;
    hydrationPromise = hydrateMissingImageFileMetadata(input.store)
      .then(count => {
        if (count > 0 && !disposed) schedule();
      })
      .finally(() => {
        hydrationPromise = undefined;
        if (hydrationRequested && !disposed) hydrateMetadata();
      });
  };

  const subscription = input.store.slots.blockUpdated.subscribe(payload => {
    if (!isFileIndexSourceFlavour(payload.flavour)) return;
    if (
      payload.type === 'update' &&
      !isFileIndexSourceUpdateKey(payload.props.key)
    ) {
      return;
    }
    if (payload.type === 'add' || payload.type === 'update') {
      announceNextFlush = true;
    }
    if (
      payload.type === 'add' ||
      (payload.type === 'update' && payload.props.key === 'sourceId')
    ) {
      hydrateMetadata();
    }
    schedule();
  });

  flush();
  hydrateMetadata();

  return {
    dispose: () => {
      disposed = true;
      clearTimeout(announceTimer);
      if (deferredRetry) {
        clearTimeout(deferredRetry);
        deferredRetry = undefined;
      }
      subscription.unsubscribe();
    },
    flush,
  };
}

function ensureFileIndexDbDataSource(
  input: EnsureFileIndexDbInput
): EnsuredFileIndexDbDataSourceResult {
  assertInput(input);
  const root = input.store.root;
  if (!root) {
    throw new Error('ensureFileIndexDb: store.root is undefined');
  }

  const databaseId = createFileIndexDatabaseId(input.docId);
  deleteNonCanonicalFileIndexHostBlocks(input.store, databaseId);
  const existingBlockId =
    input.store.getBlock(databaseId)?.model?.flavour === 'affine:database'
      ? databaseId
      : findFileIndexDatabaseBlockId(input.store, databaseId);
  const existingModel = existingBlockId
    ? (input.store.getBlock(existingBlockId)?.model as
        | DatabaseBlockModel
        | undefined)
    : undefined;
  const visible = resolveVisible(input, existingModel);
  const existingXYWH = existingModel
    ? readValidFileIndexXYWH(existingModel)
    : null;
  const existingIdentityStatus = existingModel
    ? resolveCanonicalFileIndexIdentity(input.store, existingModel, databaseId)
    : null;
  const canonicalExistingBlockId =
    existingIdentityStatus === 'valid' ||
    existingIdentityStatus === 'pending' ||
    existingIdentityStatus === 'repairable'
      ? existingBlockId
      : null;
  if (
    existingModel &&
    existingIdentityStatus !== 'pending' &&
    !canonicalExistingBlockId
  ) {
    deleteCanonicalFileIndexDatabase(input.store, databaseId);
  }

  let databaseBlockId = canonicalExistingBlockId;
  let dataSource: DatabaseBlockDataSource | undefined;
  let created = false;
  if (!databaseBlockId) {
    const initialXYWH = visible
      ? (existingXYWH ?? computeDefaultFileIndexXYWH(input.store))
      : (existingXYWH ?? DATABASE_SCHEMA_DEFAULT_XYWH);
    input.store.withoutTransact(() => {
      const provisioned = provisionShardedDatabaseBlockDataSource(input.store, {
        databaseId,
        title: FILE_INDEX_TITLE,
        parentId: root.id,
        props: {
          xywh: initialXYWH,
          canvasPresentation: createCanvasSummaryPresentationState(),
          visible,
        },
        initDataSource: writeStorageSchema,
      });
      databaseBlockId = provisioned.databaseBlockId;
      dataSource = provisioned.dataSource;
    });
    created = true;
  } else {
    if (existingModel) {
      repairMissingFileIndexCanvasGeometry(input.store, existingModel, visible);
    }
    const provisioned = provisionShardedDatabaseBlockDataSource(input.store, {
      databaseId,
      title: FILE_INDEX_TITLE,
      parentId: root.id,
      addBlock: () => databaseBlockId,
      initDataSource: writeStorageSchema,
    });
    dataSource = provisioned.dataSource;
  }
  if (!databaseBlockId) {
    throw new Error('ensureFileIndexDb: failed to create database block');
  }
  if (!dataSource) {
    throw new Error('ensureFileIndexDb: failed to create data source');
  }

  const model = input.store.getBlock(databaseBlockId)?.model as
    | DatabaseBlockModel
    | undefined;
  if (!model) {
    throw new Error(
      `ensureFileIndexDb: database model missing after ensure (${databaseBlockId})`
    );
  }

  writeProtectionProps(input.store, model, visible);
  dataSource.registerSystemDocAccessBinding();

  return {
    databaseBlockId,
    storageBlockId: dataSource.storageModel.id,
    created,
    visible,
    dataSource,
  };
}

export function ensureFileIndexDb(
  input: EnsureFileIndexDbInput
): EnsureFileIndexDbResult {
  const { dataSource: _dataSource, ...result } =
    ensureFileIndexDbDataSource(input);
  return result;
}

export function showFileIndexDb(
  input: Omit<EnsureFileIndexDbInput, 'caller' | 'visible'> & {
    xywh?: SerializedXYWH;
  }
): FileIndexDbVisibilityResult {
  const ensured = ensureFileIndexDb({
    ...input,
    visible: true,
    caller: 'user-action',
  });
  const model = input.store.getBlock(ensured.databaseBlockId)
    ?.model as DatabaseBlockModel;
  const xywh = resolveShowXYWH(input.store, model, input.xywh);
  writeProtectionProps(input.store, model, true, xywh);
  return { ...ensured, visible: true, xywh };
}

export function hideFileIndexDb(
  input: Omit<EnsureFileIndexDbInput, 'caller' | 'visible'>
): FileIndexDbVisibilityResult {
  const ensured = ensureFileIndexDb({
    ...input,
    caller: 'user-action',
  });
  const model = input.store.getBlock(ensured.databaseBlockId)
    ?.model as DatabaseBlockModel;
  const xywh =
    readCurrentFileIndexXYWH(model) ??
    readLastFileIndexXYWH(model) ??
    computeDefaultFileIndexXYWH(input.store);
  writeProtectionProps(input.store, model, false, xywh);
  return { ...ensured, visible: false, xywh };
}

export function teardownFileIndexDb(
  input: Omit<EnsureFileIndexDbInput, 'visible'>
): FileIndexDbTeardownResult {
  assertInput(input);
  if (input.caller !== 'system') {
    throw new Error('teardownFileIndexDb: caller must be system');
  }

  const databaseBlockId =
    findFileIndexDatabaseBlockId(
      input.store,
      createFileIndexDatabaseId(input.docId)
    ) ?? createFileIndexDatabaseId(input.docId);
  const model = input.store.getBlock(databaseBlockId)?.model as
    | DatabaseBlockModel
    | undefined;
  if (!model || !isFileIndexDatabase(model)) {
    return { databaseBlockId, storageBlockId: null, removed: false };
  }

  const storageModel = createDatabaseBlockDataSource(model)
    .storageModel as DatabaseBlockModel;
  const storageBlockId = storageModel.id;

  if (
    storageModel.id !== model.id ||
    storageModel.store.id !== model.store.id
  ) {
    storageModel.store.withoutTransact(() => {
      storageModel.store.deleteBlock(storageModel);
    });
  }
  input.store.withoutTransact(() => {
    input.store.deleteBlock(model);
  });

  return { databaseBlockId, storageBlockId, removed: true };
}
